import React, { useEffect, useState, forwardRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import MaterialTable, { serialNumberColumn } from '../components/MaterialTableAdapter'
import { toast } from 'react-toastify'
import moment from 'moment'
import { Trash2 } from 'lucide-react'
import { useDialog } from '../components/DialogProvider'

import Loading from '../components/Loading'
import AddBox from '@mui/icons-material/AddBox'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Check from '@mui/icons-material/Check'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Clear from '@mui/icons-material/Clear'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import Edit from '@mui/icons-material/Edit'
import FilterList from '@mui/icons-material/FilterList'
import FirstPage from '@mui/icons-material/FirstPage'
import LastPage from '@mui/icons-material/LastPage'
import Remove from '@mui/icons-material/Remove'
import SaveAlt from '@mui/icons-material/SaveAlt'
import Search from '@mui/icons-material/Search'
import ViewColumn from '@mui/icons-material/ViewColumn'
import { deleteRole, getRoles } from '../actions/roleActions'
import { DELETE_ROLE_RESET, GET_ROLES_RESET } from '../constants/roleConstants'
import { hasPermission } from '../constants/rolePermissions'
import ScreenHeader from '../components/layout/ScreenHeader'

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
}

const Roles = ({ history }) => {
  const navigate = useNavigate();
  // * States
  const [data, setData] = useState([])

  const [addRoleOption, setAddRoleOption] = useState(false)
  const [deleteRoleOption, setDeleteRoleOption] = useState(false)
  const [loading, setLoading] = useState(true)

  // * Initialization
  const dispatch = useDispatch()
  const { confirm } = useDialog()

  // * Check for auth
  const userLogin = useSelector((state) => state.userLogin)
  const { loadingUserInfo, userInfo } = userLogin

  useEffect(() => {
    // * Check if user info exists
    if (!userInfo) {
    //   history.push('/')
    }
  }, [userInfo, history])

  // * Check for role
  const getRoleInfo = useSelector((state) => state.getRoleInfo)
  const { loadingGetRole, getRoleData } = getRoleInfo

  useEffect(() => {
    if (getRoleData) {
      setAddRoleOption(hasPermission(getRoleData, 'rolesAdd'))
      setDeleteRoleOption(hasPermission(getRoleData, 'rolesDelete'))
      if (!hasPermission(getRoleData, 'rolesView')) {
        toast('Not Authorized', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
      })
        navigate('/')
      }
    }
  }, [userInfo, getRoleData, history])

  useEffect(() => {
    dispatch(getRoles())
  }, [])

  // * Get Roles
  const getRolesInfo = useSelector((state) => state.getRolesInfo)
  const { loadingGetRolesInfo, errorGetRolesInfo, getRolesData } = getRolesInfo

  useEffect(() => {
    dispatch({ type: GET_ROLES_RESET })
    if (getRolesData) {
      setLoading(false)
      setData(getRolesData)
    } else if (errorGetRolesInfo) {
      setLoading(false)
      toast(errorGetRolesInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [getRolesData, errorGetRolesInfo])

  // * Delete Role
  const deleteRoleInfo = useSelector((state) => state.deleteRoleInfo)
  const { loadingDeleteRoleInfo, errorDeleteRoleInfo, deleteRoleData } = deleteRoleInfo || {}

  useEffect(() => {
    if (deleteRoleData) {
      dispatch({ type: DELETE_ROLE_RESET })
      toast('Role deleted successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      dispatch(getRoles())
    } else if (errorDeleteRoleInfo) {
      dispatch({ type: DELETE_ROLE_RESET })
      toast(errorDeleteRoleInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 3000,
      })
    }
  }, [deleteRoleData, errorDeleteRoleInfo, dispatch])

  const handleDeleteRole = async (role) => {
    const confirmed = await confirm({
      title: 'Delete role',
      message: `Are you sure you want to delete the role "${role.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      danger: true,
    })
    if (confirmed) {
      dispatch(deleteRole(role._id))
    }
  }

  // * Users Table
  const headCells = [
    serialNumberColumn,
    {
      field: 'name',
      title: 'Name',
      headerStyle: { width: '40%' },
      cellStyle: { width: '40%' },
      render: (rowData) => (
        <Link
          className='font-bold text-md cursor-pointer text-blue-800 bg-blue-100 p-2 flex justify-center rounded'
          to={`/roles/update/${rowData._id}`}
        >
          {rowData.name}
        </Link>
      ),
    },
    {
      field: 'createdAt',
      title: 'Created At',
      headerStyle: { width: '30%' },
      cellStyle: { width: '30%' },
      render: (rowData) =>
        moment(rowData.createdAt).format('DD-MM-YYYY HH:mm:ss'),
    },
    ...(deleteRoleOption
      ? [
          {
            field: 'actions',
            title: 'Actions',
            sorting: false,
            headerClassName: 'action-column-header',
            bodyClassName: 'action-column-cell',
            headerStyle: { textAlign: 'center', width: '20%', minWidth: 90 },
            cellStyle: { textAlign: 'center', width: '20%', minWidth: 90 },
            render: (rowData) => (
              <button
                type='button'
                onClick={() => handleDeleteRole(rowData)}
                disabled={loadingDeleteRoleInfo}
                title='Delete Role'
                aria-label={`Delete role ${rowData.name}`}
                className='inline-flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-600 ring-1 ring-inset ring-red-200 transition-all hover:-translate-y-0.5 hover:bg-red-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-red-400 disabled:cursor-not-allowed disabled:opacity-50'
              >
                <Trash2 size={17} strokeWidth={2.2} />
              </button>
            ),
          },
        ]
      : []),
  ]

  if (loading) {
    return <Loading />
  }

  return (
    <div className='w-full h-full'>
      <ScreenHeader title="Roles" />
      <MaterialTable
        toolbarActions={
          addRoleOption ? (
            <Link
              className='ml-auto inline-flex h-10 items-center justify-center rounded-md bg-cm-gold px-4 text-sm font-semibold text-cm-gold-ink hover:bg-cm-gold-hover focus:outline-none focus:ring-2 focus:ring-green-400'
              to='/roles/add'
            >
              Add a Role
            </Link>
          ) : null
        }
        icons={tableIcons}
        columns={headCells}
        data={data}
        options={{
          exportButton: false,
          search: true,
          exportAllData: false,
          rowStyle: {
            height: '5px',
            fontSize: 13,
          },
          paging: true,
          pageSize: 10,
          emptyRowsWhenPaging: false,
          pageSizeOptions: [10, 20, 50],
          headerStyle: {
            position: 'sticky',
            top: '0',
          },
        }}
        onSearchChange={(searchText) => {
          // Call backend search when user types in MaterialTable search
          if (searchText) {
            dispatch(getRoles(searchText))
          } else {
            dispatch(getRoles())
          }
        }}
      />
    </div>
  )
}

export default Roles
